import React from 'react';
import {
  Users,
  Phone,
  TrendingUp,
  UserCheck,
  BarChart3,
  PieChart,
  Target,
  Clock,
  FileText,
  Calendar,
  DollarSign,
  Activity,
  MessageSquare,
  Database,
  Settings
} from 'lucide-react';

interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  route: string;
  color: string;
}

interface ReportsGridProps {
  onReportSelect: (reportId: string, reportTitle: string) => void;
}

const ReportsGrid: React.FC<ReportsGridProps> = ({ onReportSelect }) => {

  const reportCards: ReportCard[] = [
    {
      id: 'call-leaderboard',
      title: 'Call Leaderboard',
      description: 'Top performers by call volume and success rates',
      icon: <Phone size={24} />,
      category: 'Sales & Performance',
      route: '/reports/call-leaderboard',
      color: '#3b82f6'
    },
    {
      id: 'call-logs',
      title: 'Call Logs Report',
      description: 'Detailed call history and analytics',
      icon: <FileText size={24} />,
      category: 'Sales & Performance',
      route: '/reports/call-logs',
      color: '#6366f1'
    },
    {
      id: 'user-performance',
      title: 'User Performance Report',
      description: 'Individual team member performance metrics',
      icon: <TrendingUp size={24} />,
      category: 'Sales & Performance',
      route: '/reports/user-performance',
      color: '#10b981'
    },
    {
      id: 'lead-assignment',
      title: 'Lead Assignment Report',
      description: 'Lead distribution and assignment analytics',
      icon: <UserCheck size={24} />,
      category: 'Leads & Pipeline',
      route: '/reports/lead-assignment',
      color: '#f59e0b'
    },
    {
      id: 'sales-funnel',
      title: 'Sales Funnel Analysis',
      description: 'Conversion rates and pipeline analysis',
      icon: <BarChart3 size={24} />,
      category: 'Sales & Performance',
      route: '/reports/sales-funnel',
      color: '#ef4444'
    },
    {
      id: 'activity-summary',
      title: 'Activity Summary',
      description: 'Comprehensive team activity overview',
      icon: <Activity size={24} />,
      category: 'Operations',
      route: '/reports/activity-summary',
      color: '#8b5cf6'
    },
    {
      id: 'lead-source',
      title: 'Lead Source Analysis',
      description: 'Lead generation source performance',
      icon: <PieChart size={24} />,
      category: 'Leads & Pipeline',
      route: '/reports/lead-source',
      color: '#06b6d4'
    },
    {
      id: 'lead-by-labels',
      title: 'Leads by Labels',
      description: 'Lead categorization and label analytics',
      icon: <BarChart3 size={24} />,
      category: 'Leads & Pipeline',
      route: '/reports/lead-by-labels',
      color: '#8b5cf6'
    },
    {
      id: 'sales-target',
      title: 'Sales Target Tracking',
      description: 'Goal achievement and target progress',
      icon: <Target size={24} />,
      category: 'Sales & Performance',
      route: '/reports/sales-target',
      color: '#84cc16'
    },
    {
      id: 'task-status',
      title: 'Task Status Report',
      description: 'Task completion rates and pending items',
      icon: <Clock size={24} />,
      category: 'Operations',
      route: '/reports/task-status',
      color: '#f97316'
    },
    {
      id: 'custom-fields',
      title: 'Custom Field Analysis',
      description: 'Custom data field insights and trends',
      icon: <Database size={24} />,
      category: 'Operations',
      route: '/reports/custom-fields',
      color: '#64748b'
    },
    {
      id: 'quotation-status',
      title: 'Quotation Status Report',
      description: 'Quote approval rates and status tracking',
      icon: <DollarSign size={24} />,
      category: 'Business Intelligence',
      route: '/reports/quotation-status',
      color: '#dc2626'
    },
    {
      id: 'user-sync-status',
      title: 'User Sync Status',
      description: 'System synchronization and data integrity',
      icon: <Settings size={24} />,
      category: 'Operations',
      route: '/reports/user-sync-status',
      color: '#475569'
    },
    {
      id: 'trends',
      title: 'Trends & Analytics',
      description: 'Visual trends across leads, calls, activities, and sales',
      icon: <TrendingUp size={24} />,
      category: 'Business Intelligence',
      route: '/reports/trends',
      color: '#667eea'
    }
  ];

  const handleCardClick = (reportId: string, reportTitle: string) => {
    onReportSelect(reportId, reportTitle);
  };

  // Group reports by category
  const groupedReports = reportCards.reduce((groups, report) => {
    const category = report.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(report);
    return groups;
  }, {} as Record<string, ReportCard[]>);

  return (
    <div className="reports-grid-container" style={{ padding: '8px 0' }}>
      <div className="reports-header" style={{ marginBottom: '24px' }}>
        <p style={{
          fontSize: '14px',
          color: '#6b7280',
          margin: 0
        }}>
          Click on any report card to open it in a new tab
        </p>
      </div>

      {Object.entries(groupedReports).map(([category, reports]) => (
        <div key={category} className="report-category-section" style={{ marginBottom: '40px' }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '16px',
            paddingBottom: '8px',
            borderBottom: '2px solid #e5e7eb'
          }}>
            {category}
          </h3>

          <div
            className="reports-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '20px'
            }}
          >
            {reports.map((report) => (
              <div
                key={report.id}
                className="report-card"
                onClick={() => handleCardClick(report.id, report.title)}
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '24px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.15)';
                  e.currentTarget.style.borderColor = report.color;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                {/* Accent color bar */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    backgroundColor: report.color
                  }}
                />

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div
                    style={{
                      padding: '12px',
                      borderRadius: '10px',
                      backgroundColor: `${report.color}15`,
                      color: report.color,
                      flexShrink: 0
                    }}
                  >
                    {report.icon}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1f2937',
                      margin: '0 0 8px 0',
                      lineHeight: '1.4'
                    }}>
                      {report.title}
                    </h4>

                    <p style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      margin: 0,
                      lineHeight: '1.5'
                    }}>
                      {report.description}
                    </p>
                  </div>
                </div>

                {/* Click indicator */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '16px',
                    right: '16px',
                    opacity: 0.5,
                    transition: 'opacity 0.2s ease'
                  }}
                  className="click-indicator"
                >
                  <div style={{
                    fontSize: '12px',
                    color: '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    Click to view →
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <style>{`
        .report-card:hover .click-indicator {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  );
};

export default ReportsGrid;
